export type CropAspect = '1:1' | '4:5';

// Reduced integer numerator/denominator per aspect. The output canvas is snapped
// to an exact integer multiple of these so width/height equals the target ratio
// exactly — Adobe Express rejects an uploaded slide whose ratio drifts even
// slightly off target (e.g. 1023/1279 = 0.79984, just under 4:5's 0.8).
const ASPECT_RATIOS: Record<CropAspect, { w: number; h: number }> = {
	'1:1': { w: 1, h: 1 },
	'4:5': { w: 4, h: 5 },
};

/**
 * Center crop an image file to the given aspect ratio.
 * Returns the original file unchanged when it already matches.
 */
export async function centerCropToAspect(
	file: File,
	aspect: CropAspect = '1:1',
): Promise<{ blob: Blob; wasCropped: boolean }> {
	const { w: ratioW, h: ratioH } = ASPECT_RATIOS[aspect];
	const targetRatio = ratioW / ratioH;

	return new Promise((resolve, reject) => {
		const img = new Image();
		const url = URL.createObjectURL(file);

		img.onload = () => {
			URL.revokeObjectURL(url);

			const { width, height } = img;
			const sourceRatio = width / height;
			// Only skip the crop when the source is *exactly* on ratio. A float
			// tolerance would pass through a near-but-off source (e.g. 1023x1279,
			// ratio 0.79984) untouched, and Adobe Express rejects any slide whose
			// ratio drifts off 4:5 — the exact failure this crop exists to prevent.
			// Integer cross-multiply is exact where float subtraction is not.
			if (width * ratioH === height * ratioW) {
				resolve({ blob: file, wasCropped: false });
				return;
			}

			let cropWidth: number;
			let cropHeight: number;
			if (sourceRatio > targetRatio) {
				cropHeight = height;
				cropWidth = height * targetRatio;
			} else {
				cropWidth = width;
				cropHeight = width / targetRatio;
			}
			const offsetX = (width - cropWidth) / 2;
			const offsetY = (height - cropHeight) / 2;

			// Snap the output to the nearest exact integer multiple of the reduced
			// ratio so canvas.width/canvas.height is precisely the target aspect.
			// Rounding width and height independently could leave a ratio that
			// fails strict downstream validation; drawImage rescales the float crop
			// rectangle onto this exact-ratio canvas (a sub-pixel adjustment).
			const k = Math.max(1, Math.round(cropHeight / ratioH));
			const canvas = document.createElement('canvas');
			canvas.width = ratioW * k;
			canvas.height = ratioH * k;

			const ctx = canvas.getContext('2d');
			if (!ctx) {
				reject(new Error('Failed to get canvas context'));
				return;
			}

			ctx.drawImage(
				img,
				offsetX,
				offsetY,
				cropWidth,
				cropHeight,
				0,
				0,
				canvas.width,
				canvas.height,
			);

			const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
			const quality = mimeType === 'image/jpeg' ? 0.92 : undefined;

			canvas.toBlob(
				(blob) => {
					if (blob) {
						resolve({ blob, wasCropped: true });
					} else {
						reject(new Error('Failed to create blob from canvas'));
					}
				},
				mimeType,
				quality,
			);
		};

		img.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error('Failed to load image'));
		};

		img.src = url;
	});
}
